import { makeAutoObservable, runInAction } from 'mobx';
import { IType, IProduct, LocalizedString, MenuType } from '../types/entities';
import { $api } from '../http';
  
export default class ProductStore {
    _types: IType[] = [];
    _products: IProduct[] = [];
    _selectedProduct: IProduct | null = null;
    _selectedType: IType | null = null;
    _activeMenu: MenuType = 'FOOD';
    _isLoading = false; // EN: Loading state for products / PL: Stan ładowania produktów
    _error: string | null = null; // EN: Error state for products / PL: Stan błędu produktów
    
    constructor() {
        makeAutoObservable(this);
    }

    // =========================
    // PUBLIC ACTIONS
    // =========================
    clearError() {
        if (this._error !== null) {
            this._error = null;
        }
    }

    // =========================
    // HELPERS
    // =========================
    private extractError(e: any): string {
        return (
            e?.response?.data?.message ||
            e?.message ||
            "UNKNOWN_ERROR"
        );
    }

    private handleError(e: any) {
        console.error("ProductStore error:", e);
        const message = this.extractError(e);

        runInAction(() => {
            this._error = message;
        });
    }

    private startLoading() {
        this._isLoading = true;
        this._error = null;
    }

    private stopLoading() {
        this._isLoading = false;
    }

    // =========================
    // ACTIONS
    // =========================
    setTypes(types: IType[]) {
        this._types = types;
    }

    setProducts(products: IProduct[]) {
        this._products = products;
    }

    setSelectedProduct(product: IProduct | null) {
        this._selectedProduct = product;
    }

    setSelectedType(type: IType | null) {
        this._selectedType = type;
        this.fetchProducts(type ? type.id : undefined);
    }

    setActiveMenu(menu: MenuType) {
        this._activeMenu = menu;
        this._selectedType = null;
        this.fetchProducts();
    }

    // =========================
    // GETTERS
    // =========================    
    get types(): IType[] { return this._types; }
    get products(): IProduct[] { return this._products; }
    get selectedProduct(): IProduct | null { return this._selectedProduct; }
    get selectedType(): IType | null { return this._selectedType; }
    get activeMenu(): MenuType { return this._activeMenu; }
    get isLoading(): boolean { return this._isLoading; }
    get error(): string | null { return this._error; }

    get productsByType() {
        return this._products.reduce((acc, product) => {
            const typeId = product.typeId;
            if (!acc[typeId]) {
                acc[typeId] = [];
            }
            acc[typeId].push(product);
            return acc;
        }, {} as Record<number, IProduct[]>);
    }

    // =========================
    // ASYNC ACTIONS
    // =========================
    
    // EN: Fetch all products for the active menu without category filtering (useful for PDF export)
    async fetchAllProducts() {
        this.startLoading();
        try {
            const params: any = { 
                page: 1, 
                limit: 9999, 
                // menuType: this._activeMenu 
            };

            const { data } = await $api.get('/products', { params });

            runInAction(() => {
                const incomingRows = data?.rows || (Array.isArray(data) ? data : []);
                this.setProducts(incomingRows);
            });
        } catch (e) {
            this.handleError(e);
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async fetchTypes() {
        try {
            const response = await $api.get<IType[]>('/types');

            runInAction(() => {
                this.setTypes(response.data);
            });

        } catch (e) {
            runInAction(() => {
                this.setTypes([]);
            });
            this.handleError(e);
        }
    }

    async fetchTypesWithCounts() {
        try {
            const { data } = await $api.get('/types/with-counts');
            
            runInAction(() => {
                this.setTypes(Array.isArray(data) ? data : []);
            });
        } catch (e: any) {
            console.error("Error loading types with count:", e);

            runInAction(() => {
                this.setTypes([]);
            });
            this.handleError(e);
        }
    }

    async createType(data: { name: LocalizedString; code?: string; isAlcohol?: boolean }) {
        this.startLoading();

        try {
            const { data: newType } = await $api.post<IType>('/types', data);

            runInAction(() => {
                this.setTypes([newType, ...this._types]);
            });

        } catch (e) {
            this.handleError(e);
            throw e;
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async updateType(id: number, name: LocalizedString, isAlcohol?: boolean) {
        this.startLoading();

        try {
            const { data } = await $api.put(`/types/${id}`, { name, isAlcohol });
            runInAction(() => {
                this._types = this._types.map(t =>
                    t.id === id ? { ...t, name: data.name, isAlcohol: data.isAlcohol } : t
                );
            });
        } catch (e) {
            this.handleError(e);
            throw e;
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async updateTypesOrder(types: IType[]) {
        this.startLoading();
        try {
            const { data } = await $api.put('/types/reorder', { types });
            runInAction(() => {
                this.setTypes(types);
            });
            return data;
        } catch (e) {
            this.handleError(e);
            throw e;
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async deleteType(id: number) {
        this.startLoading();
        try {
            await $api.delete(`/types/${id}`);

            runInAction(() => {
                this.setTypes(this._types.filter(t => t.id !== id));
            });

        } catch (e) {
            this.handleError(e);
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async fetchProducts(typeId?: number, page = 1, limit = 999) {
        this.startLoading();

        try {
            const params: any = { 
                page, 
                limit, 
                menuType: this._activeMenu 
            };

            if (typeId) {
                params.typeId = typeId;
            }

            const { data } = await $api.get('/products', { params });

            runInAction(() => {
                // EN: Safely fall back if response payload structure changes
                const incomingRows = data?.rows || (Array.isArray(data) ? data : []);
                this.setProducts(incomingRows);
            });

        } catch (e) {
            runInAction(() => {
                this.setProducts([]);
            });

            this.handleError(e);
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async fetchOneProduct(id: number) {
        this.startLoading();

        try {
            const { data } = await $api.get<IProduct>(`/products/${id}`);

            runInAction(() => {
                this.setSelectedProduct(data);
            });

        } catch (e) {
            this.handleError(e);
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async createProduct(formData: FormData) {
        this.startLoading();

        try {
            if (!formData.has('menuType')) {
                formData.append('menuType', this._activeMenu);
            }

            const { data } = await $api.post<IProduct>('/products', formData);

            runInAction(() => {
                this._products = [data, ...this._products];
            });
            return data;
        } catch (e) {
            this.handleError(e);
            throw e; // EN: Re-throw to allow component forms to handle UI errors
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async updateProduct(id: number, formData: FormData) {
        this.startLoading();

        try {
            const { data } = await $api.put<IProduct>(`/products/${id}`, formData);

            runInAction(() => {
                this._products = this._products.map((p) => p.id === id ? data : p);
            });
            return data;
        } catch (e) {
            this.handleError(e);
            throw e; // EN: Re-throw to expose backend validation failures to the modal context
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async deleteProduct(id: number) {
        this.startLoading();

        try {
            await $api.delete(`/products/${id}`);

            runInAction(() => {
                this.setProducts(this._products.filter(p => p.id !== id));
            });

        } catch (e) {
            this.handleError(e);
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async rateProduct(productId: number, rate: number, review?: string) {
        this.startLoading();

        try {
            await $api.post('/rating', { productId, rate, review });

            await this.fetchOneProduct(productId);

        } catch (e) {
            this.handleError(e);
        } finally {
            runInAction(() => this.stopLoading());
        }
    }

    async toggleProductStatus(id: number) {
        try {
          // En: We use $authHost to ensure the request is authenticated and authorized for admin actions.
          const { data } = await $api.patch(`/products/${id}/toggle-status`);
          
          // En: We optimistically update the local product state to reflect the new availability status.
          runInAction(() => {
            const index = this._products.findIndex((p) => p.id === id);
            if (index !== -1) {
              this._products[index].isAvailable = data.isAvailable;
            }
          });
        } catch (e: any) {
            this.handleError(e);
        }
    }

    async toggleDailySpecial(id: number) {
        try {
            const { data } = await $api.patch(`/products/${id}/toggle-daily-special`);
            
            runInAction(() => {
                const index = this._products.findIndex((p) => p.id === id);
                if (index !== -1) {
                    this._products[index].isDailySpecial = data.isDailySpecial;
                }
            });
        } catch (e: any) {
            this.handleError(e);
        }
    }
}